import React, { createContext, useContext, useState, useEffect } from 'react';

type AuthContextType = {
    token: string | null;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    loading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [token, setToken] = useState<string | null>(() =>
        localStorage.getItem('token')
    );

    const [loading, setLoading] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL;

    //  RESTORE SESSION ON LOAD
    useEffect(() => {
        const storedToken = localStorage.getItem("token");

        if (!storedToken) {
            setLoading(false);
            return;
        }

        fetch(`${API_URL}/me`, {
            headers: {
                Authorization: `Bearer ${storedToken}`
            }
        })
            .then(res => {
                if (!res.ok) throw new Error("Invalid session");
                return res.json();
            })
            .then(user => {
                setToken(storedToken);
                console.log("User restored:", user);
            })
            .catch(() => {
                localStorage.removeItem("token");
                setToken(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [API_URL]);

    const login = async (username: string, password: string) => {
        const res = await fetch(`${API_URL}/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
        });

        const data = await res.json();

        if (!res.ok) {
            throw data;
        }

        setToken(data.token);
        localStorage.setItem('token', data.token);
    };

    const logout = () => {
        setToken(null);
        localStorage.removeItem('token');
    };

    return (
        <AuthContext.Provider value={{ token, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
}
